import { useState } from 'react';
import { type Lead, leadScoreConfig, leadStatuses, type LeadScore, type LeadStatus } from '../../../mocks/leads';

interface LeadDrawerProps {
  lead: Lead | null;
  onClose: () => void;
}

function ScoreBadge({ score }: { score: LeadScore }) {
  const cfg = leadScoreConfig[score];
  const icons = { hot: 'ri-fire-line', warm: 'ri-sun-line', cold: 'ri-snowflake-line' };
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
      <i className={`${icons[score]} text-[12px]`} />
      {cfg.label} Lead
    </span>
  );
}

function StatusBadge({ status }: { status: LeadStatus }) {
  const cfg = leadStatuses.find(s => s.value === status);
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ backgroundColor: `${cfg.color}18`, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

export default function LeadDrawer({ lead, onClose }: LeadDrawerProps) {
  const [activeTab, setActiveTab] = useState<'timeline' | 'info' | 'notes'>('timeline');
  const [noteText, setNoteText] = useState('');

  if (!lead) return null;

  const timelineIcons: Record<string, string> = {
    page_view: 'ri-eye-line',
    campaign_view: 'ri-megaphone-line',
    video_play: 'ri-play-circle-line',
    button_click: 'ri-cursor-line',
    whatsapp_click: 'ri-whatsapp-line',
    form_start: 'ri-file-edit-line',
    form_submit: 'ri-checkbox-circle-line',
    lead_created: 'ri-user-add-line',
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[420px] bg-white shadow-modal z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src={lead.avatar} alt={lead.name} className="w-12 h-12 rounded-full object-top object-cover border-2 border-champagne-200" />
            <div>
              <h3 className="text-[16px] font-bold text-charcoal-600">{lead.name}</h3>
              <p className="text-[12px] text-charcoal-300">{lead.phone}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-charcoal-50 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-charcoal-400 text-[18px]" />
          </button>
        </div>

        {/* Score + Status */}
        <div className="px-6 py-3 flex items-center gap-3 border-b border-border flex-shrink-0">
          <ScoreBadge score={lead.score} />
          <StatusBadge status={lead.status} />
        </div>

        {/* Quick Actions */}
        <div className="px-6 py-3 flex gap-2 flex-shrink-0 border-b border-border">
          {[
            { icon: 'ri-phone-line', label: 'Ara', color: '#7CB342' },
            { icon: 'ri-whatsapp-line', label: 'WhatsApp', color: '#25D366' },
            { icon: 'ri-user-star-line', label: 'Müşteriye Dönüştür', color: '#D4AF37' },
            { icon: 'ri-archive-line', label: 'Arşivle', color: '#A0A0A0' },
          ].map(action => (
            <button key={action.label} className="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl hover:bg-[#FAF8F4] transition-colors cursor-pointer" title={action.label}>
              <div className="w-7 h-7 flex items-center justify-center rounded-full" style={{ backgroundColor: `${action.color}18` }}>
                <i className={`${action.icon} text-[14px]`} style={{ color: action.color }} />
              </div>
              <span className="text-[9px] text-charcoal-400 font-medium whitespace-nowrap">{action.label}</span>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border flex-shrink-0">
          {[
            { id: 'timeline', label: 'Ziyaret Geçmişi' },
            { id: 'info', label: 'Lead Bilgileri' },
            { id: 'notes', label: 'Notlar' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex-1 py-3 text-[12px] font-medium transition-colors cursor-pointer whitespace-nowrap
                ${activeTab === tab.id ? 'text-champagne-600 border-b-2 border-champagne-500' : 'text-charcoal-400 hover:text-charcoal-600'}
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'timeline' && (
            <div className="p-5 space-y-1">
              <p className="text-[11px] font-semibold text-charcoal-300 uppercase tracking-wide mb-3">Dönüşüm Yolculuğu</p>
              <div className="relative">
                <div className="absolute left-[17px] top-0 bottom-0 w-px bg-border" />
                <div className="space-y-4">
                  {lead.timeline.map((event, idx) => (
                    <div key={event.id} className="flex items-start gap-3 relative">
                      <div className="w-[34px] h-[34px] flex items-center justify-center rounded-full flex-shrink-0 z-10" style={{ backgroundColor: `${event.color}18`, border: `2px solid ${event.color}30` }}>
                        <i className={`${timelineIcons[event.type] || 'ri-circle-line'} text-[13px]`} style={{ color: event.color }} />
                      </div>
                      <div className="flex-1 bg-[#FAF8F4] rounded-xl p-3 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className="text-[12px] font-semibold text-charcoal-600 leading-tight">{event.label}</p>
                          <span className="text-[10px] text-charcoal-300 flex-shrink-0 font-mono">{event.time}</span>
                        </div>
                        <p className="text-[11px] text-charcoal-400">{event.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'info' && (
            <div className="p-5 space-y-4">
              <div className="space-y-3">
                {[
                  { label: 'E-Posta', value: lead.email, icon: 'ri-mail-line' },
                  { label: 'Şehir', value: lead.city, icon: 'ri-map-pin-line' },
                  { label: 'Kampanya', value: lead.campaign, icon: 'ri-megaphone-line' },
                  { label: 'Kaynak', value: `${lead.source} / ${lead.medium || 'direct'}`, icon: 'ri-signal-tower-line' },
                  { label: 'Oluşturulma', value: lead.createdAt, icon: 'ri-time-line' },
                  { label: 'Son Etkileşim', value: lead.lastInteraction, icon: 'ri-history-line' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3 p-3 bg-[#FAF8F4] rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-champagne-50">
                      <i className={`${item.icon} text-champagne-500 text-[14px]`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-charcoal-300">{item.label}</p>
                      <p className="text-[13px] font-medium text-charcoal-600 truncate">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              {lead.tags.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-charcoal-300 uppercase tracking-wide mb-2">Etiketler</p>
                  <div className="flex flex-wrap gap-1.5">
                    {lead.tags.map(tag => (
                      <span key={tag} className="px-2.5 py-1 bg-champagne-50 text-champagne-700 rounded-full text-[11px] font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="p-5 space-y-4">
              {lead.notes && (
                <div className="bg-[#FDF8EC] border border-champagne-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-sticky-note-line text-champagne-500 text-[14px]" /></div>
                    <span className="text-[11px] font-semibold text-champagne-700">Mevcut Not</span>
                  </div>
                  <p className="text-[13px] text-charcoal-500">{lead.notes}</p>
                </div>
              )}
              <div>
                <p className="text-[12px] font-medium text-charcoal-400 mb-2">Yeni Not Ekle</p>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Not yazın..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 placeholder-charcoal-300 focus:outline-none focus:border-champagne-400 resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-charcoal-300">{noteText.length}/500</span>
                  <button className="h-[34px] px-4 bg-champagne-500 text-white rounded-xl text-[12px] font-medium hover:bg-champagne-600 transition-colors cursor-pointer whitespace-nowrap">Kaydet</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
